import { normalizeNavPath } from './navigationUtils';

/**
 * Where a relaunch should land — the whole decision, as a pure function, so it
 * can be tested without rendering a navigator.
 *
 * `NavigationRestorer` owns the React side of this (waiting for rehydration,
 * running once, checking auth) and delegates the routing call here.
 */

// Paths that are valid for restoration. Any stored path whose normalized form
// doesn't start with one of these segments is stale/invalid and gets cleared.
const VALID_PATH_PREFIXES = [
  '/recipes',
  '/grocery-list',
  '/settings',
  '/inventory',
  '/taste-profile',
  '/week-recap',
  '/day-detail',
  '/recipe-suggestions',
];

// Routes whose screen is meaningless without its params. Restoring one bare
// shows an empty shell at best; /week-recap threw RangeError out of a selector
// and took the whole app to the error boundary on launch.
const PARAM_DEPENDENT_ROUTES = ['/week-recap', '/day-detail'];

// `app=1` is not user intent — it is the flag that gets a request past the
// duchef.app -> /landing/ redirect, so it rides on the PWA start_url and on
// every landing-page CTA. Arriving that way *is* "reopened the app".
const NON_INTENT_PARAMS = new Set(['app']);

const APP_ENTRY_PATHS = ['/', '/index'];

export type RestoreDecision =
  | { action: 'stay' }
  | { action: 'clear' }
  | { action: 'restore'; path: string; params?: Record<string, string> };

export interface RestoreInput {
  /** Where the app just opened, e.g. '/' or '/week-recap'. */
  currentPathname: string;
  /** Query keys on the entry URL. Read off the address bar, not the router. */
  currentQueryKeys: string[];
  lastVisitedPath: string | null;
  lastVisitedParams: Record<string, string> | null;
  /** False when there is no stored route, or it has aged out. */
  shouldRestore: boolean;
}

function isKnownRoute(path: string): boolean {
  const normalized = normalizeNavPath(path);
  if (normalized === '/') return true;
  return VALID_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(prefix + '/'),
  );
}

export function decideRestore(input: RestoreInput): RestoreDecision {
  const { currentPathname, currentQueryKeys, lastVisitedPath, lastVisitedParams, shouldRestore } =
    input;

  const here = normalizeNavPath(currentPathname);

  // An explicit URL is a request, and it outranks whatever the user happened to
  // be looking at last. Restoration is for "reopened the app", which means
  // landing on the root having asked for nothing in particular. Without this,
  // every deep link — a weekly-email CTA, a bookmark, a shared link — is
  // replaced by the stored path and its query string is dropped.
  if (!APP_ENTRY_PATHS.includes(here)) return { action: 'stay' };
  if (currentQueryKeys.some((k) => !NON_INTENT_PARAMS.has(k))) return { action: 'stay' };

  if (!shouldRestore || !lastVisitedPath) return { action: 'stay' };

  // Home is the default; restoring it is a no-op.
  if (APP_ENTRY_PATHS.includes(normalizeNavPath(lastVisitedPath))) return { action: 'stay' };

  // Already there.
  if (normalizeNavPath(lastVisitedPath) === here) return { action: 'stay' };

  // Recipe-detail is a leaf page — recipeData is not persisted, so cold
  // restoration lands the user on a detail view without meaningful context.
  if (normalizeNavPath(lastVisitedPath).startsWith('/recipe-detail/')) return { action: 'clear' };

  // Stale paths from old builds.
  if (!isKnownRoute(lastVisitedPath)) return { action: 'clear' };

  const params = lastVisitedParams ?? {};
  const hasParams = Object.keys(params).length > 0;

  // A param-dependent route with no params is not restorable. Entries like this
  // are already on users' devices from before NavigationTracker recorded params,
  // so this has to stay a runtime check rather than only a fix upstream.
  if (!hasParams && PARAM_DEPENDENT_ROUTES.includes(normalizeNavPath(lastVisitedPath))) {
    return { action: 'clear' };
  }

  return hasParams
    ? { action: 'restore', path: lastVisitedPath, params }
    : { action: 'restore', path: lastVisitedPath };
}
